import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { LogIn } from "lucide-react";
import { authClient } from "@/auth/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const allowPasswordSignIn = process.env.NODE_ENV !== "production";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loadingProvider, setLoadingProvider] = useState<"microsoft" | "email" | null>(null);

  const signInWithMicrosoft = async () => {
    setLoadingProvider("microsoft");
    setError(null);

    const { error } = await authClient.signIn.social({
      provider: "microsoft",
      callbackURL: "/app",
    });

    if (error) {
      setError(error.message ?? "Microsoft sign in failed.");
      setLoadingProvider(null);
    } else {
      navigate({ to: "/app", search: { tab: "utilization" } });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingProvider("email");
    setError(null);

    const { error } = await authClient.signIn.email({ email, password });

    if (error) {
      setError(error.message ?? "Sign in failed. Please check your credentials.");
      setLoadingProvider(null);
    } else {
      navigate({ to: "/app", search: { tab: "utilization" } });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-2xl">Sign in</CardTitle>
          <CardDescription>Use your Microsoft work account to continue.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <Button
              type="button"
              className="w-full"
              disabled={loadingProvider !== null}
              onClick={() => void signInWithMicrosoft()}
            >
              <LogIn data-icon="inline-start" />
              {loadingProvider === "microsoft" ? "Signing in..." : "Sign in with Microsoft"}
            </Button>

            {allowPasswordSignIn && (
              <>
                <Separator />

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="outline"
                    className="w-full"
                    disabled={loadingProvider !== null}
                  >
                    {loadingProvider === "email" ? "Signing in..." : "Sign in with email"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
